import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SingleValue } from 'react-select';

import { useIsRevelioAdmin } from '@revelio/auth';
import { Button, FormField, Input, Select, Text } from '@revelio/ui';

import type {
  DeliveryFormat,
  S3DeliveryConfig,
} from '../../data-builder.store';

type S3ConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: S3DeliveryConfig | null;
  defaultBucket: string | null;
  onSave: (value: S3DeliveryConfig) => void;
};

type S3ConfigForm = {
  bucket: string;
  path: string;
  format: DeliveryFormat;
};

const formatOptions: { value: DeliveryFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'excel', label: 'Excel' },
];

export const S3ConfigModal = ({
  isOpen,
  onClose,
  initialValue,
  defaultBucket,
  onSave,
}: S3ConfigModalProps) => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<S3ConfigForm>({
    defaultValues: {
      bucket: initialValue?.bucket ?? defaultBucket ?? '',
      path: initialValue?.path ?? '',
      format: initialValue?.format ?? 'csv',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      bucket: initialValue?.bucket ?? defaultBucket ?? '',
      path: initialValue?.path ?? '',
      format: initialValue?.format ?? 'csv',
    });
  }, [defaultBucket, initialValue, isOpen, reset]);

  const onSubmit = handleSubmit((value) => {
    const bucket = isRevelioAdmin ? value.bucket : (defaultBucket ?? '');
    onSave({
      bucket,
      path: value.path,
      format: value.format,
    });
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">
          AWS S3 Bucket Configuration
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!isRevelioAdmin && (
            <Text fontSize="sm" style={{ marginBottom: '16px' }}>
              Bucket location is pre-configured for your account.
            </Text>
          )}
          {isRevelioAdmin && (
            <FormField
              label="Bucket"
              errors={
                errors.bucket?.message !== undefined
                  ? [errors.bucket.message]
                  : undefined
              }
              style={{ marginBottom: '16px' }}
            >
              <Input
                placeholder="Bucket"
                {...register('bucket', {
                  required: 'Bucket is required',
                  minLength: {
                    value: 1,
                    message: 'Minimum length should be 1',
                  },
                })}
              />
            </FormField>
          )}

          <FormField
            label="Path"
            errors={
              errors.path?.message !== undefined
                ? [errors.path.message]
                : undefined
            }
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="File Prefix (e.g. batchtime/databuilder_output/file)"
              {...register('path', {
                required: 'Path is required',
                minLength: {
                  value: 1,
                  message: 'Minimum length should be 1',
                },
                pattern: {
                  value: /^[a-zA-Z0-9_\-./]+$/,
                  message:
                    'Path can only contain letters, numbers, underscores, hyphens, periods, and forward slashes',
                },
              })}
            />
          </FormField>

          <FormField
            label="Format"
            errors={
              errors.format?.message !== undefined
                ? [errors.format.message]
                : undefined
            }
          >
            <Controller
              control={control}
              name="format"
              rules={{ required: 'Format is required' }}
              render={({ field }) => (
                <Select
                  options={formatOptions}
                  value={formatOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(
                    selectedOption: SingleValue<{
                      value: DeliveryFormat;
                      label: string;
                    }>
                  ) => {
                    field.onChange(selectedOption?.value ?? 'csv');
                  }}
                />
              )}
            />
          </FormField>
        </ModalBody>

        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={onSubmit} disabled={isSubmitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
